import { c, card } from '../../theme';
import ArcDial, { fillTowards, fillUnder } from './ArcDial';
import { changeTone, fmtChange, statusColor } from './format';

// Our status words against the arc's tones. "watch" is amber on the dial;
// there is no amber in the old Board's own three states, but the portal has a
// near-target band and dropping it would make a metric one point off target
// look identical to one ten points off.
const ARC_TONE = { good: 'good', watch: 'amber', bad: 'bad' };

// How far the figure is from its goal, in the goal's OWN units — the Board's
// format, and deliberately not the Director page's.
//
// The two pages differ and both are the old app's: the Director's gauges read
// "▲ 1.3% above goal", a share of the goal, while the Board reads "−0.6 pts
// vs goal" and "+0:34 vs goal". Service level is points, never percent of a
// percent; handle time is minutes and seconds; volume against a forecast is
// the one that is a percentage, and it says "vs FCST" rather than "vs goal".
export function goalDelta({ value, goal, unit, pct }) {
  if (value == null || goal == null || !isFinite(goal)) return null;
  const diff = value - goal;
  const sign = diff >= 0 ? '+' : '\u2212';                 // a real minus, not a hyphen
  const n = Math.abs(diff);
  if (unit === 'pct') return { text: `${sign}${n.toFixed(1)} pts vs goal`, diff };
  if (unit === 'money') return { text: `${sign}$${n.toFixed(2)} vs goal`, diff };
  if (unit === 'int') return { text: `${sign}${Math.round(n).toLocaleString('en-US')} vs goal`, diff };
  if (unit === 'fcstPct') {
    // The server has already compared these on the days the forecast covers,
    // which is not the same as dividing the whole window's Handled by it —
    // the forecast skips Sundays and holidays. Recomputing here read −2.0%
    // where the covered-days figure is −2.2%. Use its number, never our own.
    const p = pct != null ? pct : (goal > 0 ? (diff / goal) * 100 : null);
    if (p == null) return null;
    return { text: `${p >= 0 ? '+' : '\u2212'}${Math.abs(p).toFixed(1)}% vs FCST`, diff: p };
  }
  const secs = Math.round(n);
  return { text: `${sign}${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')} vs goal`, diff };
}

// One headline figure: its value (coloured by target where there is one), the
// target, the change on the prior window, the year to date, and a bar per
// month since data began, the current month coloured like the value.
export default function LeadershipKpiCard({
  title, value, status, badge, target, targetLabel = 'Target',
  change, changeUnit, better, priorLabel, ytd, months, note, fmt,
  goal, goalUnit, goalPct, goalBetter = 'up',
}) {
  const tone = changeTone(change, better);
  const delta = goalDelta({ value, goal, unit: goalUnit, pct: goalPct });
  // How full the arc runs: towards the goal for a metric that should rise,
  // and as it comes UNDER the goal for one that should fall, so a fast handle
  // time is a full arc. With no goal the arc stays empty and neutral — which
  // is what "goal under review" looks like on the old Board.
  const fill = goal == null ? 0
    : goalBetter === 'down' ? fillUnder(value, goal) : fillTowards(value, goal);
  // Beating the goal is not always "more": a handle time under target, or an
  // abandon rate under it, is the good direction.
  const beating = delta == null ? null : (goalBetter === 'down' ? delta.diff <= 0 : delta.diff >= 0);
  return (
    <section style={s.card} aria-label={title}>
      <div style={s.title}>{title}</div>
      {delta ? (
        <div style={{ ...s.goal, background: beating ? c.successTint : c.dangerTint,
          color: beating ? c.successText : c.dangerText }}>{delta.text}</div>
      ) : goal === null && (
        <div style={{ ...s.goal, background: c.surfaceSunk, color: c.textMuted }}>Goal under review</div>
      )}
      {/* The figure sits INSIDE the arc, as it does on the old Board, rather
          than above it with the arc as decoration. */}
      <ArcDial value={value} display={fmt(value)} tone={ARC_TONE[status] || null} fill={fill}
        goalFraction={goal == null ? null : 1}
        ariaLabel={`${fmt(value)}${target != null ? `, ${targetLabel} ${target}` : ''}`} />
      <div style={{ ...s.state, color: status ? statusColor(status) : c.textMuted }}>
        {status ? (status === 'good' ? 'On target' : status === 'watch' ? 'Near target' : 'Off target')
          : goal === null ? 'Goal under review' : ''}
      </div>
      {badge && <div style={{ ...s.pill, background: c.surfaceSunk, color: c.textStrong }}>{badge}</div>}
      <dl style={s.rows}>
        {target != null && (<><dt style={s.dt}>{targetLabel}</dt><dd style={s.dd}>{target}</dd></>)}
        {/* The old Board keeps the label bare and puts the comparison beside
            the number — "Change / ▲ 8.4 pp vs Aug pace" — so the three row
            labels stay the same width down the card. */}
        <dt style={s.dt}>Change</dt>
        <dd style={{ ...s.dd, color: tone ? statusColor(tone) : c.textMuted }}>
          {fmtChange(change, changeUnit)}{priorLabel ? ` vs ${priorLabel}` : ''}
        </dd>
        <dt style={s.dt}>YTD*</dt>
        <dd style={s.dd}>{fmt(ytd)}</dd>
      </dl>
      <MonthBars months={months} fmt={fmt} status={status} />
      {note && <div style={s.note}>{note}</div>}
    </section>
  );
}

// Bars on one scale from zero, so their heights compare; each carries its
// value in a tooltip, and the month under it. InContact months (before June
// 4, 2026) are striped: history from another system, never part of a total.
function MonthBars({ months, fmt, status }) {
  // A month with no figure is dropped, not drawn as an empty column. Cost per
  // call has no InContact history, so the old Board shows it as four bars from
  // June rather than nine with five blanks under them — and the same rule
  // gives speed of answer its June-onward bars without inventing five gaps.
  const shown = (months || []).filter((m) => m.value != null);
  const max = shown.length ? Math.max(...shown.map((m) => m.value)) : 0;
  if (!max) return null;
  const H = 36;
  return (
    <div style={s.bars} role="img" aria-label={shown.map((m) => `${m.label} ${fmt(m.value)}`).join(', ')}>
      {shown.map((m, i) => {
        const last = i === shown.length - 1;
        const h = Math.max(2, Math.round((m.value / max) * H));
        const fill = m.legacy
          ? `repeating-linear-gradient(135deg, ${c.borderStrong} 0 3px, ${c.surface} 3px 6px)`
          : last ? statusColor(status) : c.borderStrong;
        const note = m.legacy ? ' (InContact)' : m.partialFrom ? ' (from Jun 4)' : '';
        return (
          <div key={m.label} style={s.barCol} title={`${m.label}${note}: ${fmt(m.value)}`}>
            {/* The figure sits above its bar, as on the old Board. It is the
                thing a reader is looking for, and a tooltip cannot be seen on
                the wall-mounted screens this page is opened on. */}
            <div style={s.barValue}>{fmt(m.value)}</div>
            <div style={{ height: H, display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ ...s.bar, height: h, background: fill, border: m.legacy ? `1px solid ${c.borderStrong}` : 'none' }} />
            </div>
            <div style={s.barLabel}>{m.label}</div>
          </div>
        );
      })}
    </div>
  );
}

const s = {
  // Centred, because the arc is: a left-aligned title over a centred gauge
  // reads as two cards stacked.
  card:     { ...card.base, padding: '14px 16px 12px', display: 'flex', flexDirection: 'column',
    alignItems: 'center', gap: '6px', minWidth: 0 },
  title:    { fontSize: '10px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: c.textMuted, textAlign: 'center' },
  // "ON TARGET" under the arc. Uppercased in CSS rather than in the string, so
  // the accessible name and the tests still read the words as written.
  state:    { fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
    marginTop: '-4px', minHeight: '13px' },
  pill:     { fontSize: '10px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: '999px' },
  rows:     { display: 'grid', gridTemplateColumns: '1fr auto', gap: '4px 12px', margin: '4px 0 0', fontSize: '12px', width: '100%' },
  dt:       { color: c.textMuted },
  dd:       { margin: 0, textAlign: 'right', color: c.textStrong, fontVariantNumeric: 'tabular-nums' },
  bars:     { display: 'flex', gap: '6px', marginTop: '6px', width: '100%' },
  barValue: { fontSize: '9px', color: c.textMuted, marginBottom: '3px', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  barCol:   { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, minWidth: 0 },
  bar:      { width: '100%', maxWidth: '22px', borderRadius: '3px 3px 0 0' },
  barLabel: { fontSize: '10px', color: c.textFaint, marginTop: '3px' },
  note:     { fontSize: '11px', color: c.warningText, lineHeight: 1.4 },
};
